import FullCalendar from '@fullcalendar/react'

import interactionPlugin from '@fullcalendar/interaction'
import timeGridPlugin from '@fullcalendar/timegrid'
import dayGridPlugin from '@fullcalendar/daygrid'
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getCalendarInfo } from '../../store/appointment/appointmentActions';
import { getDeviceTimeZone } from '../../utils';
import moment from 'moment';
import { RangeEventSelected } from './rangeEventSelected';
import { CalendarColorLegend } from '../../components/calendar/CalendarColorLegend';

const CalendarView = () => {
    const cal = useRef(null);

    const [selectedKey, setSelectedKey] = useState(0);
    const [loading, setLoading] = useState(true)
    const [businessHours, setBusinessHours] = useState([]);
    const [events, setEvents] = useState([]);
    const dispatch = useDispatch();

    const [actionType, setActionType] = useState(null);
    const [currentEvent, setCurrentEvent] = useState(null);
    const [minSlice, setMinSlice] = useState(0);
    const [maxSlice, setMaxSlice] = useState(24);
    const [isOpen, setIsOpen] = useState(false);
    const [isMobileToggled, setIsMobileToggled] = useState(false);

    const getCalendarData = (isRefresh = false) => {
        dispatch(getCalendarInfo({
            timezone: getDeviceTimeZone()
        }))
            .unwrap()
            .then(v => {
                setBusinessHours(v.businessHours.days)
                setEvents(v.events)
                const maxMin = getMinMaxSlot(v.businessHours.days)
                setMinSlice(maxMin.min)
                setMaxSlice(maxMin.max)
                setLoading(false)
                if (isRefresh && cal.current) {
                    let calendarApi = cal.current.getApi();
                    calendarApi.unselect()
                }
            })

    }

    const getMinMaxSlot = (data) => {
        let minStart = Infinity;
        let maxEnd = -Infinity;

        data.forEach(day => {
            if (!day.off) {
                day.slices.forEach(slice => {
                    minStart = Math.min(minStart, slice.start);
                    maxEnd = Math.max(maxEnd, slice.end);
                });
            }
        });

        return {
            "min": minStart,
            "max": maxEnd
        }
    }
    useEffect(() => {
        getCalendarData()
    }, [])

    const selectAction = (e) => {
        setIsOpen(true)
        setActionType("DISABLE_DATE")
        setCurrentEvent(e)
        setSelectedKey((x) => x + 1)
    }

    const handleEventClick = (info) => {
        if (info.event.extendedProps?.isDisabled) return;
        selectAction(info);
    }

    const calendarBusinessHours = useMemo(() => (
        businessHours
            .filter((v) => !v.off)
            .map((v) => v.slices.map((k) => ({
                daysOfWeek: [v.day],
                startTime: k.start < 10 ? "0" + k.start + ":00" : k.start + ":00",
                endTime: k.end < 10 ? "0" + k.end + ":00" : k.end + ":00"
            })))
            .flat()
    ), [businessHours]);

    const handleEventMouseEnter = (arg) => {
        if (arg.event.extendedProps.disablePointerEvents) {
            arg.el.style.cursor = 'default';
        }
    };

    const handleEventMouseLeave = (arg) => {
        arg.el.style.cursor = '';
    };

    const selectedRangeEvents = useMemo(() => {
        if (!currentEvent?.start || !currentEvent?.end) return [];
        return events.filter((ev) => new Date(ev.date) >= currentEvent.start && new Date(ev.date) < currentEvent.end);
    }, [events, currentEvent]);

    const calendarHeight = useMemo(() => {
        if (!minSlice && !maxSlice) return 420;
        const computed = (maxSlice - minSlice) * 42;
        return Math.max(360, Math.min(computed, 560));
    }, [minSlice, maxSlice]);

    return (
        <>
            <div className='mt-5 flex gap-5'>
                <div className={`w-full ${isOpen ? 'xl:w-3/4' : 'xl:w-full'} rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:p-5`}>
                    <div className='mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-4'>
                        <div>
                            <h3 className='text-nt-blue'>Availability Calendar</h3>
                            <p className='text-sm text-nt-grey'>
                                Manage availability and blocked slots ({getDeviceTimeZone()})
                            </p>
                        </div>
                        <span className='rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600'>
                            {events.length} events
                        </span>
                    </div>

                    {loading ? (
                        <div className='grid h-[420px] place-items-center rounded-xl border border-dashed border-slate-200 bg-slate-50 text-nt-grey'>
                            Loading calendar...
                        </div>
                    ) : (
                        <div className='overflow-x-auto'>
                            <FullCalendar
                                ref={cal}
                                plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin]}
                                initialView="timeGridWeek"
                                allDaySlot={false}
                                eventOverlap={false}
                                headerToolbar={window.innerWidth > 767 ? {
                                    left: 'prev,next',
                                    center: 'title',
                                    right: 'timeGridWeek,dayGridMonth'
                                } : {
                                    left: 'prev,next',
                                    right: 'title',
                                }}
                                visibleRange={{
                                    start: new Date(),
                                    end: null
                                }}
                                firstDay={1}
                                // slotMinTime={(minSlice < 10 ? "0" + minSlice : minSlice) + ":00:00"}
                                // slotMaxTime={(maxSlice < 10 ? "0" + maxSlice : maxSlice) + ":00:00"}
                                editable={false}
                                droppable={false}
                                timeZone={getDeviceTimeZone()}
                                contentHeight={calendarHeight}
                                defaultTimedEventDuration={"00:50:00"}
                                handleWindowResize={true}
                                displayEventEnd={false}
                                slotLabelFormat={{
                                    hour: '2-digit',
                                    hour12: false,
                                    minute: '2-digit',
                                    meridiem: false
                                }}
                                eventTimeFormat={{
                                    hour: '2-digit',
                                    hour12: false,
                                    minute: '2-digit',
                                    meridiem: false
                                }}
                                events={events}
                                unselectAuto={false}
                                selectConstraint="businessHours"
                                selectOverlap={(event) => !event.extendedProps?.isDisabled}
                                selectAllow={(selectInfo) => {
                                    if (new Date(selectInfo.start) < moment()) return false;
                                    const dayOfWeek = moment(selectInfo.start).day();
                                    const day = businessHours.find((d) => d.day === dayOfWeek);
                                    return Boolean(day && !day.off);
                                }}
                                dayCellClassNames={(selectInfo) => {
                                    return new Date(selectInfo.start) < new Date() ? "disabled" : ""
                                }}
                                select={(v) => selectAction(v)}
                                eventClick={handleEventClick}
                                scrollTimeReset={false}
                                selectable={true}
                                slotDuration={'00:60:00'}
                                dragScroll={true}
                                slotLabelInterval={'00:10:00'}
                                businessHours={calendarBusinessHours}
                                eventMouseEnter={handleEventMouseEnter}
                                eventMouseLeave={handleEventMouseLeave}
                                eventClassNames={'pointer-events-none'}
                                validRange={() => {
                                    if (cal.current) {
                                        let calendarApi = cal.current.getApi();
                                        var view = calendarApi.view;
                                        if (view.type === 'dayGridMonth') {
                                            return {
                                                start: moment().utc().startOf("month").toISOString(),
                                                end: moment().utc().add(1, "year").endOf("month").toISOString()
                                            }
                                        } else if (view.type === 'timeGridWeek') {
                                            return {
                                                start: moment().utc().startOf("week").toISOString(),
                                                end: moment().utc().add(1, "year").endOf("week").toISOString()
                                            }
                                        }
                                    } else {
                                        return {
                                            start: moment().utc().startOf("week").toISOString(),
                                            end: moment().utc().add(1, "year").endOf("week").toISOString()
                                        }
                                    }
                                }}
                            />
                            <CalendarColorLegend variant="tutor" />
                        </div>
                    )}
                </div>
                <div className={`hidden ${isOpen ? 'xl:block xl:w-1/4' : 'hidden'} sticky top-24 h-fit rounded-2xl border border-slate-200 bg-white p-5 shadow-sm`}>
                    {isOpen && actionType === "DISABLE_DATE" && <RangeEventSelected key={selectedKey} setIsOpen={(v) => setIsOpen(v)} refreshCalendar={() => getCalendarData(true)} currentEvent={currentEvent} events={selectedRangeEvents} />}
                </div>
                <div className={`fixed bottom-3 z-50 overflow-y-auto rounded-2xl border border-slate-200 bg-white p-4 shadow-xl transition-all xl:hidden md:w-[calc(100%-60px)] w-[calc(100%-40px)] ${isMobileToggled ? 'max-h-[75vh]' : 'max-h-24'} ${isOpen ? 'block' : 'hidden'}`}>
                    {isOpen && actionType === "DISABLE_DATE" ? 
                        
                        (<>
                            
                            <RangeEventSelected 
                                isMobile={true}
                                isMobileToggled={isMobileToggled}
                                setIsMobileToggled={setIsMobileToggled}
                                key={selectedKey} 
                                setIsOpen={(v) => setIsOpen(v)} refreshCalendar={() => getCalendarData(true)} 
                                currentEvent={currentEvent} 
                                events={selectedRangeEvents} 
                            />
                        </>)
                    : null}
                </div>
            </div>
        </>
    )
}

export { CalendarView }

// max-height: 100px;
//     overflow-y: scroll;
//     box-shadow: -2px -2px 10px #00000020, 3px 6px 10px #00000020;